import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { IMeasurement } from "../../../@types/admin/AdminMeasurement";
import {
  IAdminPatientDetail,
  IAdminPatientListItem,
  IAdminUserDetail,
  IArchivedPatientListItem,
  IPatientAction,
  IPatientCondition,
  IPatientMedication,
  IPatientSurveyWithAnswers,
  IPatientTriggeredAlert,
} from "../../../@types/admin/AdminPatient";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { measurementsService, patientsService } from "../../../services/admin";
import {
  IAdminPatientsQuery,
  IArchivedPatientsQuery,
} from "../../../services/admin/patientsService";
import { ensureOk } from "../../../utils/ensureOk";

/** Query keys the mutations invalidate. */
export const PATIENTS_QUERY_KEY = "admin-patients";
export const ARCHIVED_PATIENTS_QUERY_KEY = "admin-archived-patients";
export const PATIENT_DETAIL_QUERY_KEY = "admin-patient-detail";
export const PATIENT_USER_QUERY_KEY = "admin-patient-user";
export const PATIENT_CONDITIONS_QUERY_KEY = "admin-patient-conditions";

/**
 * A page of patients. Server-paginated, like Measurements: this list spans
 * every patient in the region, so there is no catalogue-sized set to search
 * client-side.
 *
 * Retool's table was client-side over `getPatients.data.result`, and its query
 * sent no page params at all — so it fetched every patient and its search box
 * searched only the rows already on the page. `search` here is server-side
 * (EN-5169 added it to `GET /Retool/Patients`), and so are the hospital/ward
 * filters and the sort (EN-5277).
 */
export function useAdminPatients(query: IAdminPatientsQuery) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IPaginatedResponse<IAdminPatientListItem[]>
  >({
    // Every parameter is in the key: one left out would serve the cached
    // unfiltered page after a filter change.
    queryKey: [
      PATIENTS_QUERY_KEY,
      query.page,
      query.pageSize,
      query.search ?? "",
      query.hospitalGroupId ?? null,
      query.wardId ?? null,
      query.sortBy ?? null,
      query.sortDescending ?? false,
    ],
    queryFn: async () => ensureOk(await patientsService.getPatients(query)),
    // Keeps the previous page on screen while the next loads, so paging does
    // not flash an empty table.
    placeholderData: keepPreviousData,
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return {
    patients: data?.result ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
  };
}

/**
 * A page of discharged patients (EN-5171).
 *
 * `monitoringLength` is derived here rather than sent by the API, matching
 * Retool's own transformer on this query: how long the patient was monitored,
 * from first login to archive.
 */
export function useArchivedPatients(query: IArchivedPatientsQuery) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IPaginatedResponse<IArchivedPatientListItem[]>
  >({
    queryKey: [
      ARCHIVED_PATIENTS_QUERY_KEY,
      query.page,
      query.pageSize,
      query.search ?? "",
      query.hospitalGroupId ?? null,
      query.wardId ?? null,
    ],
    queryFn: async () =>
      ensureOk(await patientsService.getArchivedPatients(query)),
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  return {
    patients: data?.result ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
  };
}

/** One patient's record. Idle until an id is known. */
export function useAdminPatient(patientId: number | undefined) {
  const { data, isLoading, isError, error } = useQuery<IAdminPatientDetail>({
    queryKey: [PATIENT_DETAIL_QUERY_KEY, patientId],
    queryFn: async () => ensureOk(await patientsService.getPatient(patientId!)),
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { patient: data, isLoading, isError, error };
}

/**
 * The patient's linked login.
 *
 * Chained on the patient record, as Retool chained `getUserDetails` on
 * `getPatientDetails.data.userId` — the user id is not in the URL.
 */
export function useAdminPatientUser(userId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IAdminUserDetail>({
    queryKey: [PATIENT_USER_QUERY_KEY, userId],
    queryFn: async () => ensureOk(await patientsService.getUser(userId!)),
    enabled: userId !== undefined,
    refetchOnMount: "always",
  });

  return { user: data, isLoading, isError };
}

/** The patient's conditions, published first (Retool's `publish desc`). */
export function usePatientConditions(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IPatientCondition[]>({
    queryKey: [PATIENT_CONDITIONS_QUERY_KEY, patientId],
    queryFn: async () => {
      const response = ensureOk(
        await patientsService.getPatientConditions(patientId!)
      );
      // Retool ordered in SQL; the endpoint is a general-purpose list, so the
      // page's own ordering lives here.
      return [...(response.result ?? [])].sort(
        (a, b) => Number(b.publish ?? false) - Number(a.publish ?? false)
      );
    },
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { conditions: data ?? [], isLoading, isError };
}

/** The patient's protocol actions. */
export function usePatientActions(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IPatientAction[]>({
    queryKey: ["admin-patient-actions", patientId],
    queryFn: async () =>
      ensureOk(await patientsService.getPatientActions(patientId!)),
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { actions: data ?? [], isLoading, isError };
}

/** The patient's eMED medication history. */
export function usePatientMedications(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IPatientMedication[]>({
    queryKey: ["admin-patient-medications", patientId],
    queryFn: async () =>
      ensureOk(await patientsService.getPatientMedications(patientId!)),
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { medications: data ?? [], isLoading, isError };
}

/**
 * One patient's measurements, server-paginated.
 *
 * Retool built this with its own jsonb-exploding query
 * (`getMeasurementsDetails.sql`) whose whole purpose was to work out which
 * measurement columns a row had values in. `IMeasurement.measurementTypes`
 * already reports that, so this is the existing endpoint with a patient filter.
 */
export function usePatientMeasurements(
  patientId: number | undefined,
  page: number,
  pageSize: number
) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IPaginatedResponse<IMeasurement[]>
  >({
    queryKey: ["admin-patient-measurements", patientId, page, pageSize],
    queryFn: async () =>
      ensureOk(
        await measurementsService.getPatientMeasurements(
          patientId!,
          page,
          pageSize
        )
      ),
    enabled: patientId !== undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  return {
    measurements: data?.result ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
  };
}

/**
 * Alerts that have FIRED for this patient (EN-5170) — Retool's
 * `modulePatientAlerts`. Not the same thing as the configured alerts the shared
 * `AlertsPanel` shows.
 */
export function usePatientTriggeredAlerts(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IPatientTriggeredAlert[]>({
    queryKey: ["admin-patient-triggered-alerts", patientId],
    queryFn: async () => {
      const response = ensureOk(
        await patientsService.getPatientTriggeredAlerts(patientId!)
      );
      return response.result ?? [];
    },
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { triggeredAlerts: data ?? [], isLoading, isError };
}

/** The patient's completed surveys with their answers (EN-5170). */
export function usePatientSurveys(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IPatientSurveyWithAnswers[]>({
    queryKey: ["admin-patient-surveys", patientId],
    queryFn: async () =>
      ensureOk(await patientsService.getPatientSurveys(patientId!)),
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { surveys: data ?? [], isLoading, isError };
}
